import { type ReactNode, useState } from "react";
import { Link } from "react-router";
import type { TodaySummary, Visit } from "../../shared/types";
import { Empty, ErrorBanner, FlagBadges, Loading } from "../components/ui";
import { post } from "../lib/api";
import { fmtDateTime, fmtDistance, fmtDuration, fmtTime } from "../lib/format";
import { useApi } from "../lib/useApi";
import { fmtIsoDate, PageHeader, ReasonDialog, toApiError, usePolling } from "./common";

type Part = "in" | "out";

const PART_LABEL: Record<Part, string> = { in: "Clock-in", out: "Clock-out" };

/** Every punch still waiting for a decision, oldest first. */
function pendingParts(visits: Visit[]): { visit: Visit; part: Part }[] {
  const out: { visit: Visit; part: Part }[] = [];
  for (const v of visits) {
    if (v.in.result === "pending") out.push({ visit: v, part: "in" });
    if (v.out?.result === "pending") out.push({ visit: v, part: "out" });
  }
  return out.sort((a, b) => (a.visit[a.part]?.at ?? 0) - (b.visit[b.part]?.at ?? 0));
}

function Tile({
  label,
  value,
  href,
  tone,
}: {
  label: string;
  value: number;
  href: string;
  tone: "info" | "warn" | "danger" | "ok";
}) {
  return (
    <a href={href} className={`adm-tile ${value ? tone : "zero"}`}>
      <div className="stat">{value}</div>
      <div className="label">{label}</div>
    </a>
  );
}

function PendingItem({ visit, part, onDone }: { visit: Visit; part: Part; onDone: (msg: string) => void }) {
  const punch = part === "in" ? visit.in : visit.out!;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState(false);

  async function approve() {
    setBusy(true);
    setError(null);
    try {
      await post(`/admin/visits/${visit.id}/approve`, { part });
      onDone(`Approved ${visit.userName}'s ${PART_LABEL[part].toLowerCase()}.`);
    } catch (e) {
      setError(toApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <li>
      <div className="adm-item">
        <div className="main">
          <div className="title">
            {visit.userName} · {visit.siteName}
          </div>
          <div className="meta">
            {PART_LABEL[part]} at {fmtDateTime(punch.at)}
            {punch.distanceM != null && <> · {fmtDistance(punch.distanceM)} from the site</>}
            {punch.offline && <> · sent later (offline)</>}
          </div>
          <div style={{ marginTop: 4 }}>
            <FlagBadges flags={punch.flags} />
          </div>
          {punch.note && <div className="adm-note">“{punch.note}”</div>}
          {error && (
            <div className="banner danger small" style={{ marginTop: 8 }}>
              {error}
            </div>
          )}
        </div>
        <div className="actions">
          <button className="btn primary sm" onClick={approve} disabled={busy}>
            Approve
          </button>
          <button className="btn sm" onClick={() => setRejecting(true)} disabled={busy}>
            Reject
          </button>
          <Link to={`/admin/visits/${visit.id}`} className="btn ghost sm">
            Details
          </Link>
        </div>
      </div>
      {rejecting && (
        <ReasonDialog
          title={`Reject ${PART_LABEL[part].toLowerCase()}`}
          intro={
            <p>
              {visit.userName} at {visit.siteName}, {fmtDateTime(punch.at)}.{" "}
              {part === "in"
                ? "The whole visit won't count towards their hours."
                : "The visit will need a clock-out time setting before it counts."}
            </p>
          }
          placeholder="e.g. Not at the site at this time"
          confirmLabel="Reject"
          danger
          onConfirm={async (reason) => {
            await post(`/admin/visits/${visit.id}/reject`, { part, reason });
            onDone(`Rejected ${visit.userName}'s ${PART_LABEL[part].toLowerCase()}.`);
          }}
          onClose={() => setRejecting(false)}
        />
      )}
    </li>
  );
}

function VisitLine({ v, children }: { v: Visit; children?: ReactNode }) {
  return (
    <li>
      <div className="adm-item">
        <div className="main">
          <div className="title">
            <Link to={`/admin/visits/${v.id}`}>{v.userName}</Link> · {v.siteName}
          </div>
          <div className="meta">{children}</div>
        </div>
      </div>
    </li>
  );
}

export function TodayPage() {
  const today = useApi<TodaySummary>("/admin/today");
  const [message, setMessage] = useState<string | null>(null);
  usePolling(today.reload, 60_000);

  if (!today.data) {
    return (
      <div className="page">
        <PageHeader title="Today" />
        {today.error ? <ErrorBanner error={today.error} onRetry={today.reload} /> : <Loading />}
      </div>
    );
  }

  const t = today.data;
  const pending = pendingParts(t.pending);
  const done = (msg: string) => {
    setMessage(msg);
    void today.reload();
  };

  return (
    <div className="page">
      <PageHeader
        title="Today"
        sub={`${fmtIsoDate(t.date, { weekday: "long", month: "long" })} · updates every minute`}
        actions={
          <button className="btn sm" onClick={() => void today.reload()} disabled={today.loading}>
            {today.loading ? "Refreshing…" : "Refresh"}
          </button>
        }
      />
      {today.error && <ErrorBanner error={today.error} onRetry={today.reload} />}
      {message && (
        <div className="banner ok" role="status" style={{ marginBottom: 12 }}>
          <div className="row between">
            <span>{message}</span>
            <button className="btn ghost sm" onClick={() => setMessage(null)} aria-label="Dismiss">
              ✕
            </button>
          </div>
        </div>
      )}

      <div className="adm-tiles">
        <Tile label="On site now" value={t.onSite.length} href="#on-site" tone="info" />
        <Tile label="Waiting for approval" value={pending.length} href="#pending" tone="warn" />
        <Tile label="Flagged today" value={t.flaggedToday.length} href="#flagged" tone="warn" />
        <Tile label="Missing clock-outs" value={t.missingClockOut.length} href="#missing" tone="danger" />
        <Tile label="Not clocked in yet" value={t.notClockedIn.length} href="#not-in" tone="warn" />
        <Tile label="Stock requests" value={t.openStockRequests} href="/admin/stock" tone="info" />
      </div>

      <section className="adm-section card" id="pending">
        <h2>Waiting for approval</h2>
        {pending.length ? (
          <ul className="list">
            {pending.map(({ visit, part }) => (
              <PendingItem key={`${visit.id}-${part}`} visit={visit} part={part} onDone={done} />
            ))}
          </ul>
        ) : (
          <Empty>Nothing to approve.</Empty>
        )}
      </section>

      <div className="grid cols-2 adm-section">
        <section className="card" id="on-site">
          <h2>On site now</h2>
          {t.onSite.length ? (
            <ul className="list">
              {t.onSite.map((v) => (
                <VisitLine key={v.id} v={v}>
                  Since {fmtTime(v.in.at)} ({fmtDuration((Date.now() - v.in.at) / 60_000)})
                  {v.in.flags.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <FlagBadges flags={v.in.flags} />
                    </div>
                  )}
                </VisitLine>
              ))}
            </ul>
          ) : (
            <Empty>No one is clocked in.</Empty>
          )}
        </section>

        <section className="card" id="not-in">
          <h2>Not clocked in yet</h2>
          {t.notClockedIn.length ? (
            <ul className="list">
              {t.notClockedIn.map((n) => (
                <li key={`${n.userId}-${n.siteId}-${n.startTime}`}>
                  <div className="title">
                    <Link to={`/admin/people/${n.userId}`}>{n.userName}</Link> · {n.siteName}
                  </div>
                  <div className="meta muted small">Due at {n.startTime}</div>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>Everyone due so far has clocked in.</Empty>
          )}
        </section>
      </div>

      <section className="adm-section card" id="missing">
        <h2>Missing clock-outs</h2>
        {t.missingClockOut.length ? (
          <ul className="list">
            {t.missingClockOut.map((v) => (
              <li key={v.id}>
                <div className="adm-item">
                  <div className="main">
                    <div className="title">
                      {v.userName} · {v.siteName}
                    </div>
                    <div className="meta">
                      {v.out?.result === "rejected"
                        ? `Clocked in ${fmtDateTime(v.in.at)}; the clock-out at ${fmtTime(v.out.at)} was rejected.`
                        : `Clocked in ${fmtDateTime(v.in.at)} and never clocked out.`}
                    </div>
                  </div>
                  <Link to={`/admin/visits/${v.id}?edit=1`} className="btn sm">
                    Set clock-out time
                  </Link>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <Empty>None.</Empty>
        )}
      </section>

      <div className="grid cols-2 adm-section">
        <section className="card" id="flagged">
          <h2>Flagged today</h2>
          {t.flaggedToday.length ? (
            <ul className="list">
              {t.flaggedToday.map((v) => (
                <VisitLine key={v.id} v={v}>
                  {fmtTime(v.in.at)}
                  {v.out ? `–${fmtTime(v.out.at)}` : v.status === "open" ? " (still on site)" : ""}
                  {v.status === "rejected" && " · rejected"}
                  <div className="adm-cell-badges" style={{ marginTop: 4 }}>
                    <FlagBadges flags={[...new Set([...v.in.flags, ...(v.out?.flags ?? [])])]} />
                  </div>
                </VisitLine>
              ))}
            </ul>
          ) : (
            <Empty>No flags today.</Empty>
          )}
        </section>

        <section className="card" id="stickers">
          <h2>Sticker problems</h2>
          {t.stickerIssues.length ? (
            <ul className="list">
              {t.stickerIssues.map((s) => (
                <li key={s.siteId}>
                  <div className="adm-item">
                    <div className="main">
                      <div className="title">{s.siteName}</div>
                      <div className="meta">
                        {s.count} {s.count === 1 ? "clock-in or out" : "clock-ins or outs"} without a QR scan
                        in the last 14 days. The sticker may be missing or damaged.
                      </div>
                    </div>
                    <Link to={`/admin/sites/${s.siteId}`} className="btn sm">
                      Check site
                    </Link>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <Empty>No sticker problems reported.</Empty>
          )}
        </section>
      </div>
    </div>
  );
}
